import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Building2,
  Upload,
  ListOrdered,
  FileSearch,
  ScanSearch,
  Layers3,
  CircleCheck,
  Save,
  MapPin,
  Check,
  type LucideIcon,
} from 'lucide-react';
import AIAgentOrbLoader from '../../Dashboard/AIAgentOrb/AIAgentOrbLoader.tsx';
import orbStyles from '../../Dashboard/AIAgentOrb/AIAgentOrbLoader.module.scss';
import styles from './CommerceSheetJobLoader.module.scss';

export const COMMERCE_BUSY_PHASE = {
  create: '__create__',
  upload: '__upload__',
  persist: '__persist__',
  navigate: '__navigate__',
} as const;

const PIPELINE_STEPS: { code: string; label: string; Icon: LucideIcon }[] = [
  { code: 'queued', label: 'En cola para analizar el archivo', Icon: ListOrdered },
  { code: 'received', label: 'Archivo recibido', Icon: Upload },
  { code: 'extracting', label: 'Extrayendo contenido', Icon: FileSearch },
  { code: 'document_screen', label: 'Validación como lista de materiales', Icon: ScanSearch },
  { code: 'catalog_match', label: 'Preparando tu listado', Icon: Layers3 },
  { code: 'ready', label: 'Análisis del listado listo', Icon: CircleCheck },
  { code: '__persist__', label: 'Guardando tu oferta', Icon: Save },
];

function pipelineIndex(phaseCode: string): number {
  const p = phaseCode.trim().toLowerCase();
  const i = PIPELINE_STEPS.findIndex((s) => s.code === p);
  return i >= 0 ? i : -1;
}

const INLINE_LOADER_SHELL_DESKTOP_MQ = '(min-width: 901px)';

export type InlineTripSlot =
  | { kind: 'empty' }
  | { kind: 'waiting' }
  | { kind: 'mutedStep'; stepIndex: number }
  | { kind: 'done'; stepIndex: number }
  | { kind: 'active'; stepIndex: number }
  | { kind: 'upcoming'; stepIndex: number };

export function buildInlineTripSlots(
  isNavigate: boolean,
  waiting: boolean,
  activeIdx: number,
  total: number
): { slots: InlineTripSlot[]; colCount: number; edgeMask: string } {
  if (isNavigate) {
    const slots: InlineTripSlot[] = [];
    for (let c = 0; c < 3; c += 1) {
      const idx = total - 3 + c;
      slots.push(idx >= 0 ? { kind: 'done', stepIndex: idx } : { kind: 'empty' });
    }
    return { slots, colCount: 3, edgeMask: 'both' };
  }
  if (waiting) {
    const rightMuted: InlineTripSlot =
      total > 1 ? { kind: 'mutedStep', stepIndex: 1 } : { kind: 'empty' };
    return {
      slots: [{ kind: 'mutedStep', stepIndex: 0 }, { kind: 'waiting' }, rightMuted],
      colCount: 3,
      edgeMask: 'both',
    };
  }
  if (activeIdx === 0) {
    const right: InlineTripSlot =
      total > 1 ? { kind: 'upcoming', stepIndex: 1 } : { kind: 'empty' };
    return {
      slots: [{ kind: 'empty' }, { kind: 'active', stepIndex: 0 }, right],
      colCount: 3,
      edgeMask: 'both',
    };
  }
  if (activeIdx > 0) {
    const left: InlineTripSlot = { kind: 'done', stepIndex: activeIdx - 1 };
    const mid: InlineTripSlot = { kind: 'active', stepIndex: activeIdx };
    const right: InlineTripSlot =
      activeIdx < total - 1 ? { kind: 'upcoming', stepIndex: activeIdx + 1 } : { kind: 'empty' };
    return { slots: [left, mid, right], colCount: 3, edgeMask: 'both' };
  }
  return {
    slots: [{ kind: 'empty' }, { kind: 'waiting' }, { kind: 'empty' }],
    colCount: 3,
    edgeMask: 'both',
  };
}

export type CommerceSheetJobLoaderLayout = 'stack' | 'inline';

export interface CommerceSheetJobLoaderProps {
  phaseCode: string;
  fileLabel?: string | null;
  layout?: CommerceSheetJobLoaderLayout;
  inlineArtwork?: React.ReactNode;
  progressDetail?: string | null;
}

const introSweepTransition = { duration: 0.52, ease: [0.4, 0, 0.2, 1] as const };

const CommerceSheetJobLoader: React.FC<CommerceSheetJobLoaderProps> = ({
  phaseCode,
  fileLabel = null,
  layout = 'stack',
  inlineArtwork = null,
  progressDetail = null,
}) => {
  const reduceMotion = useReducedMotion() ?? false;
  const p = phaseCode.trim();
  const isCreate = p === COMMERCE_BUSY_PHASE.create;
  const isUpload = p === COMMERCE_BUSY_PHASE.upload;
  const isNavigate = p === COMMERCE_BUSY_PHASE.navigate;
  const prePipeline = isCreate || isUpload || isNavigate;
  const activeIdx = prePipeline ? -1 : pipelineIndex(p);
  const unknown = !prePipeline && activeIdx < 0 && p.length > 0;
  const total = PIPELINE_STEPS.length;
  const stepNum = activeIdx >= 0 ? activeIdx + 1 : null;
  const indeterminateBar = activeIdx === 2;
  const waitingForFirstPhase = isUpload && activeIdx < 0;
  const isInline = layout === 'inline';

  const progressPct = isCreate
    ? 6
    : isUpload
      ? 12
      : isNavigate
        ? 100
        : indeterminateBar
          ? 18
          : activeIdx >= 0
            ? Math.round(((activeIdx + 1) / total) * 100)
            : unknown
              ? 14
              : 8;

  const mainTitle = isCreate
    ? 'Registrando tu comercio'
    : isUpload
      ? 'Subiendo el listado'
      : isNavigate
        ? 'Llevándote a tu perfil'
        : unknown
          ? 'Procesando el listado'
          : activeIdx >= 0
            ? PIPELINE_STEPS[activeIdx].label
            : 'Analizando el listado';

  const fileTrim = fileLabel != null && fileLabel.trim().length > 0 ? fileLabel.trim() : null;
  const eyebrowText = isCreate
    ? 'Alta de comercio'
    : fileTrim
      ? `Archivo · ${fileTrim}`
      : 'Cargar catálogo de materiales';

  const progressTrim =
    progressDetail != null && typeof progressDetail === 'string' && progressDetail.trim().length > 0
      ? progressDetail.trim()
      : '';
  const liveStatus = isNavigate
    ? 'Redirigiendo a tu perfil.'
    : isCreate
      ? 'Registrando el comercio en Edify.'
      : isUpload
        ? 'Subiendo el archivo y encolando el análisis.'
        : unknown
          ? `Fase: ${p}.`
          : activeIdx >= 0
            ? `${PIPELINE_STEPS[activeIdx].label}. Paso ${stepNum} de ${total}.${progressTrim ? ` ${progressTrim}` : ''}`
            : `Iniciando análisis del listado.${progressTrim ? ` ${progressTrim}` : ''}`;

  const metaBlock = (
    <>
      {isCreate ? (
        <span className={styles.progressMetaMuted}>Completando el registro antes de analizar el listado.</span>
      ) : isUpload ? (
        <span className={styles.progressMetaMuted}>Enviando el archivo al servidor y esperando la primera fase del análisis.</span>
      ) : isNavigate ? (
        <span className={styles.progressMetaMuted}>Actualizando la vista de tu perfil.</span>
      ) : indeterminateBar ? (
        <span className={styles.progressMetaMuted}>
          Esta etapa puede tardar varios minutos en archivos grandes; el avance no es porcentaje exacto.
          {progressTrim ? <span className={styles.progressMetaDetail}>{progressTrim}</span> : null}
        </span>
      ) : stepNum != null ? (
        <span className={styles.progressMeta}>
          Paso {stepNum} de {total}
          {progressTrim ? <span className={styles.progressMetaDetail}>{progressTrim}</span> : null}
          <span className={styles.progressMetaHint}>No cierres esta pestaña; el proceso puede tardar varios minutos.</span>
        </span>
      ) : (
        <span className={styles.progressMetaMuted}>Esperando la primera respuesta del servidor…</span>
      )}
    </>
  );

  const showInlineArt = isInline && inlineArtwork != null && !isCreate;
  const skipIntroSweep = !showInlineArt || reduceMotion;
  const [introSweepComplete, setIntroSweepComplete] = useState(skipIntroSweep);

  useLayoutEffect(() => {
    if (skipIntroSweep) setIntroSweepComplete(true);
    else setIntroSweepComplete(false);
  }, [skipIntroSweep]);

  const loaderSplitRef = useRef<HTMLDivElement>(null);
  const [desktopShell, setDesktopShell] = useState(false);
  const [shellMinH, setShellMinH] = useState(0);

  useLayoutEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(INLINE_LOADER_SHELL_DESKTOP_MQ);
    const apply = () => setDesktopShell(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  useLayoutEffect(() => {
    if (!desktopShell) setShellMinH(0);
  }, [desktopShell]);

  useLayoutEffect(() => {
    if (!isInline || isCreate) setShellMinH(0);
  }, [isInline, isCreate]);

  const waitingTrip = waitingForFirstPhase || unknown;
  const { slots: inlineTripSlots, colCount: tripColCount, edgeMask: tripEdgeMask } = useMemo(
    () => buildInlineTripSlots(isNavigate, waitingTrip, activeIdx, total),
    [isNavigate, waitingTrip, activeIdx, total]
  );

  const tripMotionKey = useMemo(
    () =>
      `${tripColCount}|${tripEdgeMask}|${inlineTripSlots
        .map((s) => {
          if (s.kind === 'empty') return 'e';
          if (s.kind === 'waiting') return 'w';
          if (s.kind === 'mutedStep') return `m${s.stepIndex}`;
          return `${s.kind[0]}${s.stepIndex}`;
        })
        .join('.')}`,
    [inlineTripSlots, tripColCount, tripEdgeMask]
  );

  const bumpShellMinFromSplit = useCallback(() => {
    const el = loaderSplitRef.current;
    if (!el || !desktopShell || !isInline || isCreate) return;
    const h = el.offsetHeight;
    if (h > 0) setShellMinH((prev) => Math.max(prev, h));
  }, [desktopShell, isInline, isCreate]);

  useLayoutEffect(() => {
    if (!(isInline && !isCreate && desktopShell)) return;
    bumpShellMinFromSplit();
  }, [tripMotionKey, bumpShellMinFromSplit, isInline, isCreate, desktopShell]);

  useLayoutEffect(() => {
    if (!(isInline && !isCreate && desktopShell)) return undefined;
    const el = loaderSplitRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => bumpShellMinFromSplit());
    ro.observe(el);
    return () => ro.disconnect();
  }, [isInline, isCreate, desktopShell, bumpShellMinFromSplit]);

  const tripMotionPrev = useRef('');
  const prevActiveForTrip = useRef(activeIdx);
  const [tripEnterX, setTripEnterX] = useState(0);

  useLayoutEffect(() => {
    if (tripMotionPrev.current !== tripMotionKey) {
      const pa = prevActiveForTrip.current;
      setTripEnterX(activeIdx > pa ? 18 : activeIdx < pa ? -18 : 0);
      tripMotionPrev.current = tripMotionKey;
      prevActiveForTrip.current = activeIdx;
    }
  }, [tripMotionKey, activeIdx]);

  const springTransition = { type: 'spring' as const, stiffness: 380, damping: 34 };

  const orb = (
    <AIAgentOrbLoader status="thinking" className={`${orbStyles.thinkingOrb} ${styles.orb}`} />
  );

  const inlineArtClusterEl =
    showInlineArt === true ? (
      <div className={styles.inlineArtCluster} aria-hidden>
        <div className={styles.inlineArtworkInner}>{inlineArtwork}</div>
      </div>
    ) : null;

  const headerLead = inlineArtClusterEl ?? orb;

  const pipelineStepList = (
    <>
      {PIPELINE_STEPS.map(({ code, label, Icon }, i) => {
        const done = isNavigate || (activeIdx >= 0 && i < activeIdx);
        const active = !isNavigate && activeIdx >= 0 && i === activeIdx;
        const waitingSlot = (waitingForFirstPhase || unknown) && i === 0;
        const stepClass = `${styles.step} ${active ? styles.stepActive : ''} ${done ? styles.stepDone : ''} ${waitingSlot ? styles.stepWaitingServer : ''}`;
        return (
          <li key={code} className={stepClass} aria-current={active ? 'step' : undefined}>
            <span className={styles.stepIcon} aria-hidden>
              {done ? <Check size={18} strokeWidth={2.25} /> : <Icon size={18} strokeWidth={1.85} />}
            </span>
            <span className={styles.stepLabel}>{label}</span>
            {active ? <span className={styles.stepNowBadge}>En curso</span> : null}
            {waitingSlot ? <span className={styles.stepWaitingBadge}>Esperando servidor…</span> : null}
          </li>
        );
      })}
    </>
  );

  const renderInlineTripSlotInner = (slot: InlineTripSlot, i: number) => {
    if (slot.kind === 'empty') {
      return (
        <li key={`e-${i}`} className={`${styles.inlineStepTripLi} ${styles.inlineStepSlotEmpty}`} aria-hidden>
          <span className={styles.inlineStepSlotEmptyInner} />
        </li>
      );
    }
    if (slot.kind === 'waiting') {
      return (
        <li key={`w-${i}`} className={`${styles.inlineStepTripLi} ${styles.stepTrip} ${styles.stepTripWaiting}`}>
          <span className={styles.stepIcon} aria-hidden>
            <ListOrdered size={18} strokeWidth={1.85} />
          </span>
          <span className={styles.stepLabel}>Esperando servidor…</span>
        </li>
      );
    }
    if (slot.kind === 'mutedStep') {
      const { label, Icon } = PIPELINE_STEPS[slot.stepIndex];
      return (
        <li
          key={`m-${slot.stepIndex}-${i}`}
          className={`${styles.inlineStepTripLi} ${styles.stepTrip} ${styles.inlineStepTripMuted}`}
          aria-hidden
        >
          <span className={styles.stepIcon} aria-hidden>
            <Icon size={18} strokeWidth={1.85} />
          </span>
          <span className={styles.stepLabel}>{label}</span>
        </li>
      );
    }
    const { label, Icon } = PIPELINE_STEPS[slot.stepIndex];
    const done = slot.kind === 'done';
    const active = slot.kind === 'active';
    const upcoming = slot.kind === 'upcoming';
    const stepClass = [
      styles.inlineStepTripLi,
      styles.stepTrip,
      done ? styles.stepTripDone : '',
      active ? `${styles.stepTripActive} ${styles.commerceSheetTripActiveIn}` : '',
      upcoming ? styles.stepTripUpcoming : '',
    ]
      .filter(Boolean)
      .join(' ');
    return (
      <li
        key={`${slot.kind}-${slot.stepIndex}-${i}`}
        className={stepClass}
        aria-current={active ? 'step' : undefined}
      >
        <span className={styles.stepIcon} aria-hidden>
          {done ? <Check size={18} strokeWidth={2.25} /> : <Icon size={18} strokeWidth={1.85} />}
        </span>
        <span className={styles.stepLabel}>{label}</span>
        {active ? <span className={styles.stepNowBadge}>En curso</span> : null}
        {upcoming ? <span className={styles.stepUpcoming}>Siguiente</span> : null}
      </li>
    );
  };

  const inlineTripodOl = (
    <ol
      className={`${styles.inlineStepsTripodOl} ${tripColCount === 1 ? styles.inlineStepsTripodOne : styles.inlineStepsTripodGrid}`}
      aria-label="Etapas del análisis del listado"
      style={{ gridTemplateColumns: `repeat(${tripColCount}, minmax(0, 1fr))` }}
    >
      {inlineTripSlots.map((slot, i) => renderInlineTripSlotInner(slot, i))}
    </ol>
  );

  const inlineLoaderSplit = (
    <div ref={loaderSplitRef} className={styles.inlineLoaderSplit}>
      <div className={styles.inlineColLead}>
        <div className={styles.inlineTitleRow}>
          <div className={styles.inlineTitleOrb}>{orb}</div>
          <div className={styles.headerText}>
            <span className={styles.eyebrow}>{eyebrowText}</span>
            <span className={styles.title}>{mainTitle}</span>
            {metaBlock}
          </div>
        </div>
      </div>
      <div className={styles.inlineColSteps}>
        <div
          className={styles.inlineStepsViewport}
          data-trip-cols={tripColCount}
          data-trip-edge={tripEdgeMask}
        >
          {reduceMotion ? (
            <div className={styles.inlineStepsMotion}>{inlineTripodOl}</div>
          ) : (
            <motion.div
              key={tripMotionKey}
              className={styles.inlineStepsMotion}
              initial={{ opacity: 0, x: tripEnterX }}
              animate={{ opacity: 1, x: 0 }}
              transition={springTransition}
            >
              {inlineTripodOl}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );

  const inlineShellStyle: React.CSSProperties =
    desktopShell && shellMinH > 0
      ? { minHeight: shellMinH, transition: 'min-height 0.35s ease' }
      : {};

  return (
    <div
      className={`${styles.wrap} ${isInline ? styles.wrapInline : ''}`}
      aria-busy="true"
      data-edify-commerce-sheet-loader="1"
      data-layout={layout}
    >
      <div className={styles.shimmerRail} aria-hidden>
        <div className={styles.shimmer} />
      </div>
      <div className={styles.progressBarTrack} aria-hidden>
        <div className={styles.progressBarFill} style={{ width: `${progressPct}%` }} />
      </div>
      <div className={styles.srOnly} role="status" aria-live="polite" aria-atomic="true">
        {liveStatus}
      </div>
      {isInline && !isCreate ? (
        <div className={styles.inlineLoaderStage} style={inlineShellStyle}>
          {showInlineArt && !introSweepComplete ? (
            <div className={styles.inlineRevealOuter}>
              <motion.div
                className={styles.inlineRevealTrack}
                initial={{ x: reduceMotion ? '-50%' : '0%' }}
                animate={{ x: '-50%' }}
                transition={introSweepTransition}
                onAnimationComplete={() => setIntroSweepComplete(true)}
              >
                <div className={styles.inlineRevealPaneArt}>
                  <div className={styles.inlineArtCluster}>
                    <div className={styles.inlineArtworkInner}>{inlineArtwork}</div>
                  </div>
                </div>
                <div className={styles.inlineRevealPaneBody}>{inlineLoaderSplit}</div>
              </motion.div>
            </div>
          ) : (
            <div className={styles.inlineLoaderFull}>{inlineLoaderSplit}</div>
          )}
        </div>
      ) : (
        <>
          {isInline ? (
            <div className={styles.inlineHeaderBlock}>
              <div className={styles.inlineHeaderRow}>
                {headerLead}
                <div className={styles.headerText}>
                  <span className={styles.eyebrow}>{eyebrowText}</span>
                  <span className={styles.title}>{mainTitle}</span>
                  {metaBlock}
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.header}>
              {headerLead}
              <div className={styles.headerText}>
                <span className={styles.eyebrow}>{eyebrowText}</span>
                <span className={styles.title}>{mainTitle}</span>
                {metaBlock}
              </div>
            </div>
          )}
          {isCreate ? (
            <ol className={styles.steps} aria-label="Progreso del alta">
              <li className={`${styles.step} ${styles.stepActive}`} aria-current="step">
                <span className={styles.stepIcon} aria-hidden>
                  <Building2 size={18} strokeWidth={1.85} />
                </span>
                <span className={styles.stepLabel}>Registro del comercio</span>
                <span className={styles.stepNowBadge}>En curso</span>
              </li>
              <li className={styles.step}>
                <span className={styles.stepIcon} aria-hidden>
                  <MapPin size={18} strokeWidth={1.85} />
                </span>
                <span className={styles.stepLabel}>Análisis del listado (si adjuntaste archivo)</span>
              </li>
            </ol>
          ) : (
            <div className={styles.stepsScroll}>
              <ol className={styles.steps} aria-label="Etapas del análisis del listado">
                {pipelineStepList}
              </ol>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CommerceSheetJobLoader;
